import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, MapPin, GraduationCap, BookOpen, Lightbulb, Sparkles, Compass, Rocket, Layers,
  RefreshCw, Check, ArrowRight, ArrowLeft, Palette, Film, FileText, ClipboardCheck,
  Tag, Maximize2, RotateCcw, PlayCircle, PauseCircle, Save, Clock, Printer,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import VRViewer from "@/components/VRViewer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Zap, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { imgUrl, apiError } from "@/lib/api";
import { printLesson } from "@/lib/printLesson";

const STATES = ["Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia","Wisconsin","Wyoming"];
const GRADES = ["Kindergarten","1st Grade","2nd Grade","3rd Grade","4th Grade","5th Grade","6th Grade","7th Grade","8th Grade","9th Grade","10th Grade","11th Grade","12th Grade"];
const SUBJECTS = ["Science","History","Math","English Language Arts","Geography","Art"];
const STYLES = [
  { id: "Cartoon", desc: "Bold, playful, friendly shapes", emoji: "🎨" },
  { id: "Animated", desc: "Smooth 3D animation look", emoji: "✨" },
  { id: "Painted", desc: "Artistic hand-painted feel", emoji: "🖌️" },
  { id: "Realistic", desc: "Photoreal, lifelike detail", emoji: "🌍" },
];
const OUTLINE_META = {
  virtual_lecture: { label: "Virtual Lecture", icon: GraduationCap, color: "from-indigo-500 to-blue-500", testid: "outline-card-virtual-lecture" },
  virtual_tour: { label: "Virtual Tour", icon: Compass, color: "from-cyan-500 to-teal-500", testid: "outline-card-virtual-tour" },
  immersive_experience: { label: "Immersive Experience", icon: Rocket, color: "from-violet-500 to-fuchsia-500", testid: "outline-card-immersive-exp" },
};
const STEP_LABELS = ["Setup", "Outlines", "Style", "Documents", "VR Lesson"];

function Loading({ msg }) {
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center animate-in-up" data-testid="generator-loading">
      <div className="relative">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-2xl shadow-primary/30 animate-pulse">
          <Sparkles className="w-9 h-9 text-white" />
        </div>
      </div>
      <p className="mt-6 text-lg font-display font-semibold">{msg}</p>
      <p className="text-muted-foreground mt-1 text-sm">Crafting standards-aligned content with AI…</p>
      <Loader2 className="w-6 h-6 animate-spin text-primary mt-5" />
    </div>
  );
}

export default function Generator() {
  const nav = useNavigate();
  const [phase, setPhase] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [form, setForm] = useState({ state: "", grade: "", subject: "", topic: "" });
  const [outlinesData, setOutlinesData] = useState(null);
  const [shownTitles, setShownTitles] = useState([]);
  const [chosen, setChosen] = useState(null);
  const [style, setStyle] = useState("");
  const [custom, setCustom] = useState("");
  const [documents, setDocuments] = useState(null);
  const [vrScene, setVrScene] = useState(null);
  const [vrKey, setVrKey] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [savedLessonId, setSavedLessonId] = useState(null);
  const [students, setStudents] = useState([]);
  const [usage, setUsage] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [depthLoading, setDepthLoading] = useState(false);
  const { user } = useAuth();
  const canvasWrap = useRef(null);

  const loadUsage = () => api.get("/usage").then((r) => setUsage(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data)).catch(() => {});
    loadUsage();
  }, []);

  const addDepthGen = async () => {
    if (!savedLessonId) return;
    setDepthLoading(true);
    toast.info("Adding spatial depth — generating layers & clean backdrops (~1 min/scene)…");
    try {
      await api.post(`/lessons/${savedLessonId}/spatialize`);
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get(`/lessons/${savedLessonId}/spatial-status`);
          if (data.spatial_status === "done") {
            clearInterval(poll); setDepthLoading(false);
            const fresh = await api.get(`/lessons/${savedLessonId}`);
            setVrScene(fresh.data.vr_scene);
            setVrKey((k) => k + 1);
            toast.success("Depth added — explore the layered scene!");
          } else if (data.spatial_status === "error") {
            clearInterval(poll); setDepthLoading(false);
            toast.error("Couldn't add depth — check the LLM budget and try again");
          }
        } catch (e) { /* keep polling */ }
      }, 5000);
    } catch (e) { setDepthLoading(false); toast.error("Couldn't start depth generation"); }
  };


  const doUpgrade = async () => {
    setUpgradeLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: "vividvr_classroom_monthly",
        origin_url: window.location.origin,
        user_id: user?.id || null,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Couldn't start upgrade checkout");
      setUpgradeLoading(false);
    }
  };

  const setField = (k) => (v) => setForm({ ...form, [k]: v });

  const genOutlines = async (exclude = []) => {
    setLoading(true);
    setLoadingMsg("Designing 3 lesson outlines");
    try {
      const { data } = await api.post("/generate/outlines", { ...form, exclude });
      setOutlinesData(data);
      setShownTitles((prev) => [...prev, ...data.outlines.map((o) => o.title)]);
      setPhase(2);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const submitForm = (e) => {
    e.preventDefault();
    if (!form.state || !form.grade || !form.subject || !form.topic) {
      toast.error("Please fill in all fields");
      return;
    }
    setShownTitles([]);
    genOutlines([]);
  };

  const genDocuments = async () => {
    setLoading(true);
    setLoadingMsg("Generating storyboards, scripts & assessments");
    try {
      const { data } = await api.post("/generate/documents", { outline: chosen, style, custom, ...form });
      setDocuments(data);
      setPhase(4);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const approveAndBuild = async () => {
    if (usage && !usage.unlimited && usage.remaining <= 0) {
      setUpgradeOpen(true);
      return;
    }
    setLoading(true);
    setLoadingMsg("Building your 3D VR world");
    try {
      const { data } = await api.post("/generate/vr", { outline: chosen, style, ...form, storyboard: documents.storyboard });
      setVrScene(data);
      // auto-save the lesson
      const saved = await api.post("/lessons", {
        title: chosen.title,
        state: form.state, grade: form.grade, subject: form.subject, topic: form.topic,
        style, outline: chosen, documents, vr_scene: data, status: "ready",
      });
      setSavedLessonId(saved.data.id);
      loadUsage();
      toast.success("VR lesson built & saved to your dashboard!");
      setPhase(5);
    } catch (e) {
      if (e.response?.status === 402) {
        setUpgradeOpen(true);
        loadUsage();
      } else {
        toast.error(apiError(e.response?.data?.detail) || "Generation failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleFullscreen = () => {
    const el = canvasWrap.current;
    if (!document.fullscreenElement) el?.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Stepper */}
        <div className="flex items-center justify-center mb-10">
          <div className="flex items-center gap-1 sm:gap-2">
            {STEP_LABELS.map((label, i) => {
              const n = i + 1;
              const active = phase === n;
              const done = phase > n;
              return (
                <div key={label} className="flex items-center">
                  <div className="flex flex-col items-center gap-1.5">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      done ? "bg-primary text-primary-foreground" : active ? "bg-primary text-primary-foreground ring-4 ring-primary/20" : "bg-secondary text-muted-foreground"
                    }`}>
                      {done ? <Check className="w-4 h-4" /> : n}
                    </div>
                    <span className={`text-[11px] font-semibold hidden sm:block ${active ? "text-foreground" : "text-muted-foreground"}`}>{label}</span>
                  </div>
                  {i < STEP_LABELS.length - 1 && <div className={`w-8 sm:w-14 h-0.5 mx-1 mb-4 ${phase > n ? "bg-primary" : "bg-border"}`} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Usage meter */}
        {usage && (
          <div className="max-w-2xl mx-auto -mt-4 mb-8" data-testid="usage-meter">
            {usage.unlimited ? (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Zap className="w-4 h-4 text-primary" /> <span className="font-medium capitalize">{usage.plan}</span> plan · unlimited VR lesson builds
              </div>
            ) : (
              <div className="rounded-2xl border border-border bg-card px-5 py-3">
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="font-medium flex items-center gap-1.5"><Zap className="w-4 h-4 text-primary" /> <span className="capitalize">{usage.plan}</span> plan</span>
                  <span className="text-muted-foreground" data-testid="usage-count">{usage.used} of {usage.limit} builds used this month</span>
                </div>
                <Progress value={Math.min(100, (usage.used / usage.limit) * 100)} className="h-2" />
                {usage.remaining <= 1 && (
                  <button data-testid="usage-upgrade-link" onClick={() => setUpgradeOpen(true)} className="text-xs text-primary font-semibold mt-2 flex items-center gap-1 hover:underline">
                    <TrendingUp className="w-3.5 h-3.5" /> {usage.remaining <= 0 ? "You're out of builds — upgrade to keep going" : "Almost out — upgrade to Classroom for 25/month"}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {loading && <Loading msg={loadingMsg} />}

        {/* PHASE 1 */}
        {!loading && phase === 1 && (
          <div className="max-w-2xl mx-auto animate-in-up">
            <div className="text-center mb-8">
              <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight">Let's build a VR lesson</h1>
              <p className="text-muted-foreground mt-2">Four quick fields — that's all we need to start.</p>
            </div>
            <form onSubmit={submitForm} className="rounded-3xl border border-border bg-card p-8 space-y-6 shadow-sm">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="flex items-center gap-2 mb-2"><MapPin className="w-4 h-4 text-primary" /> State</Label>
                  <Select value={form.state} onValueChange={setField("state")}>
                    <SelectTrigger data-testid="wizard-step1-state-select" className="h-11"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent className="max-h-72">{STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2"><GraduationCap className="w-4 h-4 text-primary" /> Grade level</Label>
                  <Select value={form.grade} onValueChange={setField("grade")}>
                    <SelectTrigger data-testid="wizard-step1-grade-select" className="h-11"><SelectValue placeholder="Select grade" /></SelectTrigger>
                    <SelectContent className="max-h-72">{GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2"><BookOpen className="w-4 h-4 text-primary" /> Subject</Label>
                  <Select value={form.subject} onValueChange={setField("subject")}>
                    <SelectTrigger data-testid="wizard-step1-subject-select" className="h-11"><SelectValue placeholder="Select subject" /></SelectTrigger>
                    <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="flex items-center gap-2 mb-2"><Lightbulb className="w-4 h-4 text-primary" /> Topic</Label>
                  <Input data-testid="wizard-step1-topic-input" placeholder="e.g. The Solar System" value={form.topic} onChange={(e) => setField("topic")(e.target.value)} className="h-11" />
                </div>
              </div>
              <Button type="submit" data-testid="wizard-step1-submit-btn" className="w-full h-12 rounded-full text-base">
                <Sparkles className="w-5 h-5 mr-2" /> Generate lesson outlines
              </Button>
            </form>
          </div>
        )}

        {/* PHASE 2 */}
        {!loading && phase === 2 && outlinesData && (
          <div className="animate-in-up">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-display font-extrabold tracking-tight">Choose your lesson</h1>
              <p className="text-muted-foreground mt-2">{form.topic} · {form.grade} · {form.subject} · {form.state}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {outlinesData.outlines.map((o) => {
                const meta = OUTLINE_META[o.type] || OUTLINE_META.virtual_lecture;
                const Icon = meta.icon;
                return (
                  <div key={o.id} data-testid={meta.testid} className="rounded-3xl border border-border bg-card overflow-hidden flex flex-col hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className={`bg-gradient-to-br ${meta.color} p-5 text-white`}>
                      <Icon className="w-7 h-7 mb-2" />
                      <div className="text-xs font-bold uppercase tracking-widest opacity-90">{meta.label}</div>
                      <h3 className="text-lg font-display font-bold mt-1 leading-tight">{o.title}</h3>
                    </div>
                    <div className="p-5 flex flex-col flex-1">
                      <p className="text-sm italic text-primary">"{o.hook}"</p>
                      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{o.summary}</p>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-3"><Clock className="w-3.5 h-3.5" /> {o.duration}</div>
                      <div className="mt-3">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Key scenes</div>
                        <ul className="space-y-1">
                          {(o.key_scenes || []).slice(0, 3).map((s, i) => (
                            <li key={i} className="text-sm flex gap-2"><span className="text-primary">•</span> {s}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-4">
                        {(o.standards || []).map((s, i) => (
                          <Badge key={i} variant="secondary" className="font-mono text-[10px]">{s}</Badge>
                        ))}
                      </div>
                      <Button data-testid="outline-select-btn" onClick={() => { setChosen(o); setPhase(3); }} className="mt-5 w-full rounded-full">
                        Choose this lesson <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center gap-3 mt-8">
              <Button variant="outline" onClick={() => setPhase(1)} className="rounded-full"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button variant="secondary" data-testid="outline-regenerate-btn" onClick={() => genOutlines(shownTitles)} className="rounded-full">
                <RefreshCw className="w-4 h-4 mr-1" /> Regenerate 3 more
              </Button>
            </div>
          </div>
        )}

        {/* PHASE 3 */}
        {!loading && phase === 3 && (
          <div className="max-w-3xl mx-auto animate-in-up">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-3"><Palette className="w-3.5 h-3.5" /> Visual style</div>
              <h1 className="text-3xl font-display font-extrabold tracking-tight">Pick a look for "{chosen?.title}"</h1>
              <p className="text-muted-foreground mt-2">This defines how your 3D world and storyboards are rendered.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {STYLES.map((s) => (
                <button
                  key={s.id}
                  data-testid={`style-select-${s.id.toLowerCase()}`}
                  onClick={() => setStyle(s.id)}
                  className={`text-left rounded-2xl border-2 p-5 transition-all ${style === s.id ? "border-primary bg-primary/5 shadow-lg shadow-primary/10" : "border-border bg-card hover:border-primary/40"}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-3xl">{s.emoji}</span>
                    {style === s.id && <Check className="w-5 h-5 text-primary" />}
                  </div>
                  <div className="font-display font-bold text-lg mt-3">{s.id}</div>
                  <div className="text-sm text-muted-foreground">{s.desc}</div>
                </button>
              ))}
            </div>
            <div className="mt-6">
              <Label className="mb-2 block">Any special requests? (optional)</Label>
              <Textarea data-testid="style-custom-input" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. Emphasize hands-on experiments, include a friendly robot guide, keep vocabulary simple…" className="min-h-24" />
            </div>
            <div className="flex justify-center gap-3 mt-8">
              <Button variant="outline" onClick={() => setPhase(2)} className="rounded-full"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button data-testid="style-submit-btn" disabled={!style} onClick={genDocuments} className="rounded-full px-8">
                Generate supporting documents <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* PHASE 4 */}
        {!loading && phase === 4 && documents && (
          <div className="animate-in-up">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-display font-extrabold tracking-tight">Review your lesson materials</h1>
              <p className="text-muted-foreground mt-2">Everything's ready. Approve to build the VR world.</p>
            </div>
            <Tabs defaultValue="storyboard" className="w-full">
              <TabsList className="grid grid-cols-2 sm:grid-cols-4 w-full max-w-2xl mx-auto mb-6">
                <TabsTrigger value="storyboard" data-testid="documents-storyboard-tab"><Film className="w-4 h-4 mr-1.5" /> Storyboard</TabsTrigger>
                <TabsTrigger value="script" data-testid="documents-script-tab"><FileText className="w-4 h-4 mr-1.5" /> Script</TabsTrigger>
                <TabsTrigger value="assessment" data-testid="documents-assessments-tab"><ClipboardCheck className="w-4 h-4 mr-1.5" /> Quiz</TabsTrigger>
                <TabsTrigger value="standards" data-testid="documents-standards-tab"><Tag className="w-4 h-4 mr-1.5" /> Standards</TabsTrigger>
              </TabsList>

              <TabsContent value="storyboard">
                <div className="grid md:grid-cols-2 gap-6">
                  {documents.storyboard.map((sc, i) => (
                    <div key={i} className="rounded-2xl border border-border bg-card overflow-hidden">
                      <div className="aspect-video bg-secondary relative overflow-hidden">
                        {sc.image_url ? (
                          <img src={imgUrl(sc.image_url)} alt={sc.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Film className="w-8 h-8" /></div>
                        )}
                        <Badge className="absolute top-3 left-3 bg-black/70 text-white border-0">Scene {sc.scene}</Badge>
                      </div>
                      <div className="p-4">
                        <h4 className="font-display font-semibold">{sc.title}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{sc.description}</p>
                        <p className="text-sm mt-3 italic border-l-2 border-primary pl-3">{sc.narration}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="script">
                <div className="max-w-3xl mx-auto space-y-3">
                  {documents.script.map((line, i) => (
                    <div key={i} className="rounded-xl border border-border bg-card p-4">
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="font-semibold">{line.speaker}</Badge>
                        <span className="text-xs text-muted-foreground">Scene {line.scene}</span>
                      </div>
                      <p className="mt-2">{line.lines}</p>
                      {line.audio_cue && <p className="text-xs text-muted-foreground mt-2 font-mono">🔊 {line.audio_cue}</p>}
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="assessment">
                <div className="max-w-3xl mx-auto">
                  <h3 className="font-display font-bold text-xl mb-4">{documents.assessment?.title}</h3>
                  <div className="space-y-4">
                    {(documents.assessment?.questions || []).map((q, i) => (
                      <div key={i} className="rounded-xl border border-border bg-card p-5">
                        <p className="font-semibold">{i + 1}. {q.question}</p>
                        <div className="grid sm:grid-cols-2 gap-2 mt-3">
                          {q.options.map((opt, j) => {
                            const letter = opt.trim()[0];
                            const correct = String(q.answer).trim().toUpperCase().startsWith(letter?.toUpperCase());
                            return (
                              <div key={j} className={`text-sm rounded-lg px-3 py-2 border ${correct ? "border-emerald-500 bg-emerald-50 text-emerald-800 font-semibold" : "border-border"}`}>
                                {opt} {correct && <Check className="w-3.5 h-3.5 inline ml-1" />}
                              </div>
                            );
                          })}
                        </div>
                        {q.explanation && <p className="text-xs text-muted-foreground mt-3">💡 {q.explanation}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="standards">
                <div className="max-w-3xl mx-auto">
                  <Accordion type="single" collapsible className="space-y-3">
                    {(documents.standards_explained || []).map((st, i) => (
                      <AccordionItem key={i} value={`s${i}`} className="rounded-xl border border-border bg-card px-4">
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-3 text-left">
                            <Badge className="font-mono bg-primary/10 text-primary border-0">{st.code}</Badge>
                            <span className="font-semibold">{st.title}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <p className="text-sm"><span className="font-semibold">What it means: </span>{st.explanation}</p>
                          <p className="text-sm mt-2"><span className="font-semibold">How this lesson meets it: </span>{st.how_addressed}</p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex justify-center gap-3 mt-10">
              <Button variant="outline" onClick={() => setPhase(3)} className="rounded-full"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button data-testid="approve-generate-vr-btn" onClick={approveAndBuild} className="rounded-full px-8 h-12 text-base">
                <Rocket className="w-5 h-5 mr-2" /> Approve & build VR lesson
              </Button>
            </div>
          </div>
        )}

        {/* PHASE 5 */}
        {!loading && phase === 5 && vrScene && (
          <div className="animate-in-up">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 text-xs font-bold uppercase tracking-wider mb-3"><Check className="w-3.5 h-3.5" /> Lesson ready & saved</div>
              <h1 className="text-3xl font-display font-extrabold tracking-tight">{chosen?.title}</h1>
              <p className="text-muted-foreground mt-2">Drag to look around · click hotspots & characters to interact</p>
            </div>
            <div ref={canvasWrap} data-testid="vr-canvas-container" className="relative rounded-3xl overflow-hidden border border-slate-800 bg-[#0B0F19] h-[540px]">
              <VRViewer key={vrKey} scene={vrScene} autoRotate={autoRotate} quiz={documents?.assessment?.questions || []} lessonId={savedLessonId} students={students} />
              <div className="absolute top-4 left-4 glass rounded-xl px-4 py-2">
                <div className="text-xs font-bold uppercase tracking-wider text-primary">{vrScene.environment?.name}</div>
                <div className="text-xs text-muted-foreground">{style} style</div>
              </div>
              <div className="absolute top-4 right-4 flex gap-2">
                <Button size="icon" variant="secondary" data-testid="vr-play-pause-btn" onClick={() => setAutoRotate((a) => !a)} className="rounded-full">
                  {autoRotate ? <PauseCircle className="w-5 h-5" /> : <PlayCircle className="w-5 h-5" />}
                </Button>
                <Button size="icon" variant="secondary" data-testid="vr-reset-camera-btn" onClick={() => setVrKey((k) => k + 1)} className="rounded-full">
                  <RotateCcw className="w-5 h-5" />
                </Button>
                <Button size="icon" variant="secondary" data-testid="vr-toggle-fullscreen-btn" onClick={toggleFullscreen} className="rounded-full">
                  <Maximize2 className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Button variant="outline" onClick={() => { setPhase(1); setForm({ state: "", grade: "", subject: "", topic: "" }); setChosen(null); setStyle(""); setDocuments(null); setVrScene(null); }} className="rounded-full">
                <Sparkles className="w-4 h-4 mr-1" /> Build another
              </Button>
              <Button variant="secondary" data-testid="vr-print-pack-btn" onClick={() => printLesson({ title: chosen?.title, ...form, style, outline: chosen, documents })} className="rounded-full">
                <Printer className="w-4 h-4 mr-1" /> Print lesson pack
              </Button>
              <Button variant="secondary" data-testid="vr-add-depth-btn" onClick={addDepthGen} disabled={depthLoading} className="rounded-full">
                {depthLoading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Adding depth…</> : <><Layers className="w-4 h-4 mr-1" /> Add depth (beta)</>}
              </Button>
              <Button data-testid="vr-go-dashboard-btn" onClick={() => nav("/dashboard")} className="rounded-full px-6">
                <Save className="w-4 h-4 mr-1" /> Go to dashboard
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Upgrade nudge dialog */}
      <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
        <DialogContent className="max-w-md" data-testid="upgrade-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" /> You've hit your monthly limit</DialogTitle>
            <DialogDescription>
              {usage ? `Your ${usage.plan} plan includes ${usage.limit} VR lesson builds per month.` : ""} Upgrade to <strong>Classroom</strong> for <strong>25 builds/month</strong>, VR export and rostering — just $99/mo.
            </DialogDescription>
          </DialogHeader>
          <ul className="space-y-2 text-sm py-2">
            {["25 VR lesson builds / month", "Classroom license — 30 students", "VR export & rostering", "Cancel anytime"].map((f) => (
              <li key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600" /> {f}</li>
            ))}
          </ul>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="ghost" onClick={() => setUpgradeOpen(false)} data-testid="upgrade-later-btn">Maybe later</Button>
            <Button onClick={doUpgrade} disabled={upgradeLoading} data-testid="upgrade-now-btn" className="rounded-full">
              {upgradeLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Zap className="w-4 h-4 mr-1" /> Upgrade to Classroom</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
